import type { ShaderGenerationErrorCode, ShaderGenerationPhase } from '@/lib/canvas/shader/authoring/types'

export interface GenerationNotice {
  /** What went wrong, in the user's terms. */
  title: string
  /** The way out, when there is one. */
  fix?: string
  /** Compiler output shown verbatim in a monospace block. */
  detail?: string
}

/** The origin Ollama must allow; the page's own when it runs in a browser. */
function pageOrigin(): string {
  return typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'https://app.genie.art'
}

/** The sentence the engine appends when a failed repair on Genie credits was paid for twice. */
const CHARGED = /Genie credits were charged/

/** Turns a failed generation into a message with the fix that matches its cause. */
export function describeGenerationFailure(code: ShaderGenerationErrorCode | undefined, message: string): GenerationNotice {
  switch (code) {
    case 'AUTH':
      // The engine's message names the way out: add a key, fix a rejected one, or sign in to Genie for credits.
      return { title: 'Not signed in, or the key is missing or rejected.', fix: message.trim() || 'Add your key in Node controls.' }
    case 'CORS':
      return {
        title: 'Your local model blocked this page.',
        // The engine's message names the exact origin and the macOS steps; keep it when it has them.
        fix: message.includes('OLLAMA_ORIGINS') ? message : `Restart Ollama allowing this site: OLLAMA_ORIGINS=${pageOrigin()} ollama serve`,
      }
    case 'PRICING_NOT_FOUND':
      return { title: 'This model has no Genie price yet, so nothing was charged.', fix: 'Pick another model, or use your own OpenRouter key.' }
    case 'INSUFFICIENT_BALANCE':
      return { title: 'Not enough credits for this model.', fix: 'Top up your wallet, or use your own OpenRouter key.' }
    case 'VALIDATION':
      // GL info logs can end in a NUL; it renders as a stray glyph.
      return { title: 'The new code would not run, so the previous shader is still playing.', detail: message.split('\u0000').join('').trim() }
    case 'RATE_LIMITED':
      return { title: 'The provider is rate limiting requests.', fix: message.trim() || 'Wait a moment and try again.' }
    case 'NETWORK':
      return { title: 'Could not reach the provider.', fix: message }
    case 'EMPTY':
      return {
        title: 'The model answered without any code.',
        // On Genie credits a failed repair says both attempts were charged; keep that.
        fix: CHARGED.test(message) ? `Try rephrasing, or pick a stronger model. ${message.slice(message.search(CHARGED)).trim()}` : 'Try rephrasing, or pick a stronger model.',
      }
    case 'TRUNCATED':
      // The engine says whether the context window or the output budget ran out, and what to change.
      return { title: 'The model stopped before the shader was finished.', fix: message.trim() || 'Ask for a simpler effect, or pick another model.' }
    case 'ABORTED':
      return { title: 'Cancelled.' }
    default:
      return { title: message || 'Shader generation failed.' }
  }
}

const PHASE_LABELS: Record<ShaderGenerationPhase, string> = {
  generating: 'Writing…',
  validating: 'Checking…',
  repairing: 'Fixing…',
}

export function generationPhaseLabel(phase: ShaderGenerationPhase | undefined): string {
  return phase ? PHASE_LABELS[phase] : PHASE_LABELS.generating
}
